import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { User } from "./model/login";
import { Company } from "./model/Company";

@Injectable({
  providedIn: "root",
})
export class DataServiceService {
  apiUrl: string;
  localUrl = "http://localhost:3000/api/v1/";
  devUrl = "http://54.164.64.42:3000/api/v1/";
  prodUrl = "https://api.ikyc.app/api/v1/";

  constructor(private _http: HttpClient) {
    this.apiUrl = this.prodUrl;
  }

  //USER ROUTES
  getUserDetails(email: string) {
    return this._http.get<User>(this.apiUrl + `users/${email}`).toPromise();
  }

  updateUserBillingDetails(userBillingInfo: any) {
    return this._http
      .put(this.apiUrl + `users/updatebillinginfo`, userBillingInfo)
      .toPromise();
  }

  updateUserBasicDetails(userBasicInfo: any) {
    return this._http
      .put(this.apiUrl + `users/updatebasicinfo`, userBasicInfo)
      .toPromise();
  }

  updateUserCompanyDetails(userCompanyInfo: any) {
    return this._http
      .put(this.apiUrl + `users/updatecompanyinfo`, userCompanyInfo)
      .toPromise();
  }

  // PEP SEARCH ROUTES
  getQuickSearch(search) {
    return this._http
      .post<any>(this.apiUrl + `quicksearch`, search)
      .toPromise();
  }

  getAdvanceSearch(searchObj: any) {
    return this._http
      .post<any>(this.apiUrl + `advancesearch`, searchObj)
      .toPromise();
  }

  //Save Search
  saveSearchResult(result: any) {
    return this._http.post<any>(this.apiUrl + `saveresult`, result).toPromise();
  }

  getUserHistory(email: string) {
    return this._http.get<any>(this.apiUrl + `saveresult/${email}`).toPromise();
  }

  saveAdverseMediaHistory(result: any) {
    return this._http
      .post<any>(this.apiUrl + `adverse-media-history`, result)
      .toPromise();
  }

  getAdverseMediaHistory(email: string) {
    return this._http
      .get<any>(this.apiUrl + `adverse-media-history/${email}`)
      .toPromise();
  }

  getAdverseMediaHistoryById(historyId: string) {
    return this._http
      .get<any>(this.apiUrl + `adverse-media-history/detail/${historyId}`)
      .toPromise();
  }

  uploadBatchScanReport(userId: string, userEmail: string, file: File, title?: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("userEmail", userEmail);
    if (title) {
      formData.append("title", title);
    }
    return this._http
      .post<any>(this.apiUrl + `batch-scan-reports/upload`, formData)
      .toPromise();
  }

  getBatchScanReportsByUserId(userId: string) {
    return this._http
      .get<any>(this.apiUrl + `batch-scan-reports/admin/user/${userId}`)
      .toPromise();
  }

  getBatchScanReports(email: string) {
    return this._http
      .get<any>(this.apiUrl + `batch-scan-reports/${email}`)
      .toPromise();
  }

  getBatchScanReportDownloadUrl(reportId: string) {
    return this._http
      .get<any>(this.apiUrl + `batch-scan-reports/download/${reportId}`)
      .toPromise();
  }

  deleteBatchScanReport(reportId: string) {
    return this._http
      .delete<any>(this.apiUrl + `batch-scan-reports/admin/${reportId}`)
      .toPromise();
  }

  // NSL SEARCH ROUTES
  getNSLQuickSearch(search) {
    return this._http
      .post<any>(this.apiUrl + `nsl-quicksearch`, search)
      .toPromise();
  }

  getNSLAdvanceSearch(searchObj: any) {
    return this._http
      .post<any>(this.apiUrl + `nsl-advancesearch`, searchObj)
      .toPromise();
  }

  // ENTITY SEARCH ROUTES
  getEntityQuickSearch(search) {
    return this._http
      .post<any>(this.apiUrl + `entity-quicksearch`, search)
      .toPromise();
  }

  // NSL ENTITY SEARCH ROUTES
  getNSLEntityQuickSearch(search) {
    return this._http
      .post<any>(this.apiUrl + `nsl-entity-quicksearch`, search)
      .toPromise();
  }

  // ADVERSE MEDIA SEARCH ROUTES
  getAdverseMediaSearch(search: {
    name: string;
    fetch_articles?: boolean;
    isEntity?: boolean;
    start_date?: string;
  }) {
    return this._http
      .post<any>(this.apiUrl + `adverse-media-search`, search)
      .toPromise();
  }

  //LOGIN ROUTES
  getAuthToken(loginInfo) {
    return this._http
      .post<string>(this.apiUrl + `login/generateToken`, loginInfo)
      .toPromise();
  }

  forgotPassword(email: string) {
    return this._http
      .post<any>(this.apiUrl + `login/forgot-password`, { email })
      .toPromise();
  }

  resetPassword(payload: {
    email: string;
    resetCode: string;
    password: string;
  }) {
    return this._http
      .post<any>(this.apiUrl + `login/reset-password`, payload)
      .toPromise();
  }

  //PARTNER LOGIN ROUTES (external partners)
  getPartnerAuthToken(loginInfo) {
    return this._http
      .post<any>(this.apiUrl + `login/partner/generateToken`, loginInfo)
      .toPromise();
  }

  // PARTNER SEARCH ROUTES (external partners)
  // Unified quick search for entities/individuals:
  // body: { name|keyword, isEntity, isChineseSearch }
  partnerQuickSearch(search: any) {
    return this._http
      .post<any>(this.apiUrl + `partner-quicksearch`, search)
      .toPromise();
  }

  // Partner advance search
  partnerAdvanceSearch(search: any) {
    return this._http
      .post<any>(this.apiUrl + `partner-advancesearch`, search)
      .toPromise();
  }

  //USER MANAGEMENT ROUTES
  getTotalUserCount() {
    return this._http.get<any>(this.apiUrl + `usersmgt/usercount`).toPromise();
  }

  getAllUsers() {
    return this._http.get<any>(this.apiUrl + `usersmgt/users`).toPromise();
  }

  getAllAdmins() {
    return this._http.get<any>(this.apiUrl + `usersmgt/adminusers`).toPromise();
  }

  getUserById(userId: string) {
    return this._http
      .get<any>(this.apiUrl + `usersmgt/user/${userId}`)
      .toPromise();
  }

  getAdminUserById(userId: string) {
    return this._http
      .get<any>(this.apiUrl + `usersmgt/adminuser/${userId}`)
      .toPromise();
  }

  getUserDetailsById(userId: string) {
    return this._http
      .get<any>(this.apiUrl + `usersmgt/details/${userId}`)
      .toPromise();
  }

  updateUserStatus(userId, userData) {
    return this._http
      .patch<any>(this.apiUrl + `usersmgt/update-user-status`, {
        userId,
        ...userData,
      })
      .toPromise();
  }

  updateUserPassword(userId, userData) {
    return this._http
      .patch<any>(this.apiUrl + `usersmgt/update-user-password`, {
        userId,
        ...userData,
      })
      .toPromise();
  }

  updateUser(userId, userData) {
    return this._http
      .patch<any>(this.apiUrl + `usersmgt/update-user`, { userId, ...userData })
      .toPromise();
  }

  updateAdverseMediaEnabled(userId, isAdverseMediaEnabled: boolean) {
    return this._http
      .patch<any>(this.apiUrl + `usersmgt/update-adverse-media`, {
        userId,
        isAdverseMediaEnabled,
      })
      .toPromise();
  }

  updateLastLogin(userId) {
    return this._http
      .patch<any>(this.apiUrl + `usersmgt/update-last-login`, {
        userId: userId,
      })
      .toPromise();
  }

  addUser(user: User) {
    return this._http.post<any>(this.apiUrl + `usersmgt`, user).toPromise();
  }

  addAdmin(user: User) {
    return this._http
      .post<any>(this.apiUrl + `usersmgt/admin`, user)
      .toPromise();
  }

  //COMPANY MANAGEMENT ROUTES
  getTotalCompanyCount() {
    return this._http
      .get<any>(this.apiUrl + `companymgt/companycount`)
      .toPromise();
  }

  getAllCompanies() {
    return this._http
      .get<any>(this.apiUrl + `companymgt/companies`)
      .toPromise();
  }

  getCompanyById(companyId: string) {
    return this._http
      .get<any>(this.apiUrl + `companymgt/company/${companyId}`)
      .toPromise();
  }

  getCompanyNameById(companyId: string) {
    return this._http
      .get<any>(this.apiUrl + `companymgt/company-name/${companyId}`)
      .toPromise();
  }

  updateCompany(companyId, companyData) {
    return this._http
      .patch<any>(this.apiUrl + `companymgt/update-company`, {
        companyId,
        ...companyData,
      })
      .toPromise();
  }

  addCompany(company: Company) {
    return this._http
      .post<any>(this.apiUrl + `companymgt`, company)
      .toPromise();
  }

  updateCompanyStatus(companyId, companyData) {
    return this._http
      .patch<any>(this.apiUrl + `companymgt/update-company-status`, {
        companyId,
        ...companyData,
      })
      .toPromise();
  }

  //SEARCH CREDITS ROUTES
  checkAvailableCredits() {
    return this._http
      .get<any>(this.apiUrl + `search-credits/check`)
      .toPromise();
  }

  increaseSearchCredit(count = 1) {
    return this._http
      .post<any>(this.apiUrl + `search-credits/increase`, {
        msg: "increase user count",
        count,
      })
      .toPromise();
  }

  incrementDailyCounter() {
    return this._http
      .post<any>(this.apiUrl + `search-credits/increment-daily-counter`, {})
      .toPromise();
  }

  getCreditInfo() {
    return this._http
      .get<any>(this.apiUrl + `search-credits/creditinfo`)
      .toPromise();
  }

  topupCredits(creditData) {
    return this._http
      .patch<any>(this.apiUrl + `search-credits/topup`, creditData)
      .toPromise();
  }
}
